// --- Dependencies -------------------------------------------------------------------------------
import { MenuItem, TextField } from "@mui/material";
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse } from "@/types";
import OutlinedField from "@/UI/components/OutlinedField";
import ImagePicker from "@/UI/inputs/ImagePicker";
import { getTextFieldSx, withCacheBust } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    onClose: () => void;
};

type TyGender = "male" | "female" | "";

type TyMe = {
    _id: string;
    name: string;
    phone: string;
    picture: string | null;
    gender: "male" | "female" | null;
};

// --- Logic --------------------------------------------------------------------------------------

export default function EditProfilePopup({ onClose }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const client = useAuth((state) => state.client);
    const updateClient = useAuth((state) => state.updateClient);

    const textFieldSx = getTextFieldSx(lang === "ar" ? "rtl" : "ltr");

    const { data: meData, loading: meLoading } = useFetch<TyApiResponse<{ client: TyMe }>>("/clients/me");
    const {
        data: saveData,
        loading: saving,
        error: saveError,
        invoke: save,
    } = useFetch<TyApiResponse<{ client: TyMe }>>("/clients/me", { method: "PUT" }, false);

    const me = meData?.data?.client ?? null;

    const [name, setName] = useState("");
    const [gender, setGender] = useState<TyGender>("");
    const [picture, setPicture] = useState<File | null>(null);
    const [pictureCleared, setPictureCleared] = useState(false);

    // Seed the form once the server copy lands.
    useEffect(() => {
        if (!me) return;
        setName(me.name ?? "");
        setGender(me.gender ?? "");
        setPicture(null);
        setPictureCleared(false);
    }, [me]);

    // The profile screen reads the identity straight from `useAuth`, so refreshing it there is enough
    // — the popup closes and the header, avatar and chips are already the saved values.
    useEffect(() => {
        if (!saveData?.success) return;
        const updated = saveData.data.client;
        // The upload reuses the same S3 key, so the URL alone would still resolve to the cached image.
        const freshPicture = withCacheBust(updated.picture);
        if (client) updateClient({ ...client, name: updated.name, picture: freshPicture, gender: updated.gender });
        onClose();
    }, [saveData]);

    const currentPicture = pictureCleared ? null : (me?.picture ?? null);
    const dirty = Boolean(me) && (name !== (me?.name ?? "") || gender !== (me?.gender ?? "") || Boolean(picture) || pictureCleared);
    const busy = saving || meLoading;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!dirty || !name.trim() || busy) return;
        save({
            data: {
                name: name.trim(),
                gender: gender || null,
                ...(picture ? { newPicture: picture } : pictureCleared ? { picture: null } : {}),
            },
        });
    };

    return (
        <div className="edit-profile-popup">
            <div className="edit-profile-popup-header">
                <p className="edit-profile-popup-title">{t.settings.accountTitle}</p>
                <button type="button" className="edit-profile-popup-close" onClick={onClose} aria-label={t.settings.cancelButton}>
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>

            <form className="epp-body" onSubmit={handleSubmit} noValidate>
                <p className="epp-subtitle">{t.settings.accountSubtitle}</p>

                <div className="epp-field">
                    <OutlinedField label={t.settings.pictureLabel}>
                        <ImagePicker
                            url={currentPicture}
                            file={picture}
                            onPick={(file) => {
                                setPicture(file);
                                setPictureCleared(false);
                            }}
                            onRemove={() => {
                                setPicture(null);
                                setPictureCleared(true);
                            }}
                            changeLabel={t.settings.changePicture}
                            removeLabel={t.settings.removePicture}
                            placeholder="/icons/client-small-placeholder-icon.svg"
                            shape="circle"
                            disabled={busy}
                        />
                    </OutlinedField>
                </div>

                <div className="epp-field">
                    <TextField
                        fullWidth
                        label={t.settings.nameLabel}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={busy}
                        sx={textFieldSx}
                    />
                </div>

                <div className="epp-field">
                    <TextField
                        select
                        fullWidth
                        label={t.settings.genderLabel}
                        value={gender}
                        onChange={(e) => setGender(e.target.value as TyGender)}
                        disabled={busy}
                        sx={textFieldSx}
                    >
                        <MenuItem value="male">{t.settings.genderMale}</MenuItem>
                        <MenuItem value="female">{t.settings.genderFemale}</MenuItem>
                    </TextField>
                </div>

                <div className="epp-field">
                    <TextField
                        fullWidth
                        disabled
                        label={t.settings.phoneLabel}
                        value={me?.phone ?? client?.phone ?? ""}
                        slotProps={{ htmlInput: { dir: "ltr" } }}
                        sx={textFieldSx}
                    />
                    <p className="epp-field-hint">{t.settings.phoneHint}</p>
                </div>

                {saveError && <p className="epp-error">{saveError}</p>}

                <button type="submit" className="epp-submit" disabled={busy || !dirty || !name.trim()}>
                    {saving ? <span className="epp-spinner" /> : t.settings.saveButton}
                </button>
            </form>
        </div>
    );
}
